import { useLayoutEffect, useRef, useState } from 'react';
import { useSpring, animated } from '@react-spring/web';

export type UsageScope = 'window' | 'cycle';

const OPTIONS: { value: UsageScope; label: string }[] = [
  { value: 'window', label: 'Window' },
  { value: 'cycle', label: 'This cycle' },
];

export interface WindowCycleToggleProps {
  value: UsageScope;
  onChange: (value: UsageScope) => void;
}

// Mirrors TimeRangeToggle's animated-pill style for visual consistency across
// the org-admin usage panel's toggles.
export function WindowCycleToggle({ value, onChange }: WindowCycleToggleProps) {
  const index = OPTIONS.findIndex((option) => option.value === value);
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [metrics, setMetrics] = useState({ left: 0, width: 0 });

  useLayoutEffect(() => {
    const measure = () => {
      const el = buttonRefs.current[index];
      if (el) setMetrics({ left: el.offsetLeft, width: el.offsetWidth });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [index]);

  const pillSpring = useSpring({
    left: metrics.left,
    width: metrics.width,
    config: { tension: 400, friction: 30 },
  });

  return (
    <div className="relative inline-flex rounded-full bg-muted p-0.5">
      <animated.span
        className="absolute bottom-0.5 top-0.5 rounded-full bg-card shadow-sm"
        style={{ left: pillSpring.left, width: pillSpring.width }}
        aria-hidden
      />
      {OPTIONS.map((option, i) => (
        <button
          key={option.value}
          ref={(el) => {
            buttonRefs.current[i] = el;
          }}
          type="button"
          onClick={() => onChange(option.value)}
          className={[
            'relative z-10 whitespace-nowrap rounded-full px-3.5 py-1.5 text-center text-xs font-medium transition-colors duration-150',
            value === option.value ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          ].join(' ')}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
